"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, CARD } from "@/components/tool-kit/styles";
import { ResultsPanel } from "@/components/tools/biology-genetics/protein-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import { parseFasta } from "@/lib/tool-kit/biology-genetics/fasta";
import {
  type AnalysisSettings,
  analyseProtein,
  type PkaSet,
  parseProtein,
} from "@/lib/tool-kit/biology-genetics/protein-properties";

/** English strings. A translated content file overrides these through `ui`. */
export const PROTEIN_MOLECULAR_WEIGHT_CALCULATOR_STRINGS = {
  inputLabel: "Protein sequence — plain text or FASTA",
  inputHint:
    "One-letter codes. Spaces, line breaks, numbers and lower case are fine. Several FASTA records each get a row in the table.",
  inputPlaceholder: ">my_protein\nMKVLAAGIVGLLLA…",
  exampleHeader:
    "LYSC_CHICK Lysozyme C, mature chain (UniProt P00698, residues 19–147)",
  tryExample: "Try an example",
  optionsHeading: "Options",
  acetylation: "N-terminus acetylated",
  amidation: "C-terminus amidated",
  disulfidesLabel: "Number of disulfide bonds",
  disulfidesHint:
    "Each bond removes 2 H from the mass and two free thiols from the charge. At most half the cysteines.",
  pkaLabel: "pKa set for the charge and pI",
  pkaBjellqvist: "Bjellqvist (ExPASy Compute pI/Mw)",
  pkaEmboss: "EMBOSS iep",
  phLabel: "pH for the net charge",
  results: "Results",
  unnamed: "Sequence {n}",
  recordLabel: "Sequence to show",
  averageMass: "Molecular weight (average)",
  monoMass: "Monoisotopic mass",
  length: "Length",
  pi: "Isoelectric point (pI)",
  chargeAt: "Net charge at pH {ph}",
  unitDa: "Da",
  unitKda: "kDa",
  unitAa: "aa",
  protonated: "[M+H]⁺ monoisotopic m/z",
  formula: "Elemental formula",
  extinctionHeading: "Extinction coefficient at 280 nm",
  extinctionNote:
    "In water, from Pace et al. (1995): 5,500 per Trp, 1,490 per Tyr, 125 per cystine.",
  rowColumn: "Assumption",
  epsilonColumn: "ε (M⁻¹ cm⁻¹)",
  absorbanceColumn: "A280 of 1 mg/mL",
  reducedRow: "All Cys reduced",
  cystineRow: "{count} cystines (your disulfide bonds)",
  cystineAssumed: "{count} cystines (every pair of Cys assumed bonded)",
  compositionHeading: "Amino acid composition",
  residueColumn: "Residue",
  threeColumn: "Three-letter",
  countColumn: "Count",
  percentColumn: "Percent",
  summaryHeading: "All sequences",
  nameColumn: "Sequence",
  lengthColumn: "Length",
  massColumn: "Mass (Da)",
  copySummary: "Copy table (TSV)",
  copyResult: "Copy result",
  copied: "Copied!",
  summaryLine:
    "{name}: {length} aa, average {average} Da, monoisotopic {mono} Da, pI {pi} ({pka}), ε280 {epsilon} M⁻¹ cm⁻¹",
  ambiguous:
    "{count} ambiguous residue codes (B, J, X, Z) were left out, so the mass is that of the remaining residues only.",
  dropped: "Ignored characters that are not letters: {list}.",
  disulfidesCapped:
    "The number of disulfide bonds was reduced to {count}, the most the cysteines allow.",
  prompt: "Paste a protein sequence to calculate its properties.",
  errorNone: "No amino acid letters were found in that text.",
  errorDisulfides:
    "Enter the number of disulfide bonds as a whole number of 0 or more.",
  errorPh: "Enter a pH between 0 and 14.",
  basicWarning:
    "Predicted pI values are less reliable for very basic proteins and small peptides; treat them as estimates.",
} as const;

const EXAMPLE =
  "KVFGRCELAAAMKRHGLDNYRGYSLGNWVCAAKFESNFNTQATNRNTDGSTDYGILQINSRWWCNDGRTPGSRNLCNIPCSALLSSDITASVNCAKKIVSDGNGMNAWVAWRNRCKGTDVQAWIRGCRL";

export default function ProteinMolecularWeightCalculatorWidget() {
  const strings = useToolUi(PROTEIN_MOLECULAR_WEIGHT_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState(`>${strings.exampleHeader}\n${EXAMPLE}`);
  const [acetylation, setAcetylation] = useState(false);
  const [amidation, setAmidation] = useState(false);
  const [bondsRaw, setBondsRaw] = useState("4");
  const [pkaId, setPkaId] = useState<PkaSet["id"]>("bjellqvist");
  const [phRaw, setPhRaw] = useState("7.4");
  const [picked, setPicked] = useState("0");

  const bonds = parseIntegerInput(bondsRaw);
  const ph = parseNumberInput(phRaw);
  const phValid = ph !== null && ph >= 0 && ph <= 14;
  const records = parseFasta(text).map((record, index) => ({
    name: record.name || t(strings.unnamed, { n: fmt(index + 1) }),
    parsed: parseProtein(record.sequence),
  }));
  const proteins = records.filter((record) => record.parsed.length > 0);
  const selectedIndex = Math.min(
    Number(picked),
    Math.max(proteins.length - 1, 0)
  );
  const current = proteins[selectedIndex];

  const settings: AnalysisSettings = { acetylation, amidation, bonds, pkaId };
  const rows = proteins.map((record) => ({
    record,
    ...analyseProtein(record.parsed, settings),
  }));
  const view = current ? rows[selectedIndex] : null;
  const dropped = [
    ...new Set(records.flatMap((record) => record.parsed.dropped)),
  ];
  const ambiguous = records.reduce(
    (sum, record) => sum + record.parsed.ambiguous,
    0
  );

  let message: string | null = null;
  if (text.trim() === "") {
    message = strings.prompt;
  } else if (proteins.length === 0) {
    message = strings.errorNone;
  }

  const summaryTsv = [
    [
      "name",
      "length",
      "average_mass",
      "monoisotopic_mass",
      "pI",
      "epsilon_280_reduced",
      "epsilon_280_cystines",
    ].join("\t"),
    ...rows.map((row) =>
      [
        row.record.name,
        row.record.parsed.length,
        row.average.toFixed(3),
        row.mono.toFixed(4),
        row.pi.toFixed(2),
        row.epsilon.reduced,
        row.epsilon.withCystines,
      ].join("\t")
    ),
  ].join("\n");

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="pmw-input"
        label={strings.inputLabel}
        onChange={(value) => {
          setText(value);
          setPicked("0");
        }}
        placeholder={strings.inputPlaceholder}
        rows={7}
        value={text}
      />
      <button
        className={BUTTON_SECONDARY}
        onClick={() => {
          setText(`>${strings.exampleHeader}\n${EXAMPLE}`);
          setBondsRaw("4");
          setAcetylation(false);
          setAmidation(false);
        }}
        type="button"
      >
        {strings.tryExample}
      </button>
      <div className={`${CARD} space-y-4`}>
        <h2 className="font-medium text-foreground text-sm">
          {strings.optionsHeading}
        </h2>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={acetylation}
            label={strings.acetylation}
            onChange={setAcetylation}
          />
          <Checkbox
            checked={amidation}
            label={strings.amidation}
            onChange={setAmidation}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            hint={strings.disulfidesHint}
            id="pmw-bonds"
            label={strings.disulfidesLabel}
            mono
            numeric
            onChange={setBondsRaw}
            value={bondsRaw}
          />
          <SelectField
            id="pmw-pka"
            label={strings.pkaLabel}
            onChange={setPkaId}
            options={[
              { value: "bjellqvist", label: strings.pkaBjellqvist },
              { value: "emboss", label: strings.pkaEmboss },
            ]}
            value={pkaId}
          />
          <TextField
            id="pmw-ph"
            label={strings.phLabel}
            mono
            numeric
            onChange={setPhRaw}
            value={phRaw}
          />
        </div>
      </div>
      {dropped.length > 0 ? (
        <Notice>{t(strings.dropped, { list: dropped.join(" ") })}</Notice>
      ) : null}
      {ambiguous > 0 ? (
        <Notice>{t(strings.ambiguous, { count: fmt(ambiguous) })}</Notice>
      ) : null}
      {bonds === null || bonds < 0 ? (
        <Notice>{strings.errorDisulfides}</Notice>
      ) : null}
      {phValid ? null : <Notice>{strings.errorPh}</Notice>}
      {message ? <Notice>{message}</Notice> : null}
      {view && current ? (
        <ResultsPanel
          current={current}
          fmt={fmt}
          ph={phValid ? ph : null}
          picked={picked}
          pkaId={pkaId}
          proteins={proteins}
          rows={rows}
          selectedIndex={selectedIndex}
          setPicked={setPicked}
          strings={strings}
          summaryTsv={summaryTsv}
          view={view}
        />
      ) : null}
    </div>
  );
}
